import React from 'react';
import {Field, useForm} from 'react-final-form';
import {FormFieldRadioButton} from '@yandex-data-ui/cloud-components';

import {ComputeFormFieldsValues} from 'components/calculator/ComputeFormFields/ComputeFormFields';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import {
    findClosestValue,
    getCoreFractionOptions,
    getPlatformCoresVariants,
    getPlatformMemoryVariants,
} from 'components/calculator/ComputeFormFields/utils';
import {ComputeConfig} from 'components/calculator/models';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

interface CoreFractionFieldProps extends WithTranslationProps {
    name: string;
    platforms: ComputeConfig['platforms'];
    values: ComputeFormFieldsValues;
}

const CoreFractionField: React.FC<CoreFractionFieldProps> = ({name, platforms, values, i18nK}) => {
    const {change} = useForm<ComputeFormFieldsValues>();

    const handleCoreFractionChange = React.useCallback(
        (newCoreFraction: string) => {
            if (!newCoreFraction) {
                return;
            }

            const {platform, cores, memory} = values;
            const platformData = platforms.find(({id}) => id === platform);
            let newCores = cores;

            if (!platformData) {
                return;
            }

            const platformCoresVariants = getPlatformCoresVariants(
                platformData,
                Number(newCoreFraction),
            );

            if (!platformCoresVariants.includes(cores)) {
                newCores = findClosestValue(platformCoresVariants, cores);
                change(`${name}cores` as keyof ComputeFormFieldsValues, newCores);
            }

            const platformMemoryVariants = getPlatformMemoryVariants(
                platformData,
                Number(newCoreFraction),
                newCores,
            );

            if (!platformMemoryVariants.includes(memory)) {
                const closestMemory = findClosestValue(platformMemoryVariants, memory);
                change(`${name}memory` as keyof ComputeFormFieldsValues, closestMemory);
            }
        },
        [change, name, platforms, values],
    );

    return (
        <CalculatorFormLayout.Row
            title={i18nK('field_core-fraction')}
            note={i18nK('context_core-fraction')}
        >
            <Field name={`${name}coreFraction`} type="radio" parse={Number} format={String}>
                {(fieldProps) => (
                    // @ts-ignore RadioButton doesn't accept Number type as a value
                    <FormFieldRadioButton
                        {...fieldProps}
                        size="l"
                        options={getCoreFractionOptions(platforms, values.platform)}
                        width="max"
                        onUpdate={handleCoreFractionChange}
                    />
                )}
            </Field>
        </CalculatorFormLayout.Row>
    );
};

export default withTranslation('calculator')(CoreFractionField);
